import { nodePathToFriendlyName, cancelCodeInformationalArray } from '@ourbranch/lookups';
import { policyDetailsFromSegments, getMiAutoPipChanges, getUmbrellaEligibility } from '@ourbranch/policy-utils';
import up from 'core/assets/svg/up.svg';
import down from 'core/assets/svg/down.svg';
import { toCapitalized } from 'core/helpers/formatters';
import { getUmbrellaBIMinimum } from 'core/helpers/quoter.service';
import { PolicyChangeAction } from 'core/helpers/policy-status';
import { DRAFT_STATUS } from 'core/helpers/policy-drafting';

export function getPriceDetails({ newTotalPrice, originalTotalPrice }) {
  if (newTotalPrice > originalTotalPrice) {
    return {
      arrow: up,
      label: 'increase'
    };
  }
  if (newTotalPrice < originalTotalPrice) {
    return {
      arrow: down,
      label: 'decrease'
    };
  }
  return {
    arrow: null,
    label: 'not change'
  };
}

/**
 *
 * @param {string} changes - the node paths of the changes in a comma separated string
 * @param {array} additionalItems - user friendly strings to also be displayed as items changed to the user
 * @returns
 */
export function parseCoverageChanges(changes, additionalItems) {
  const changedNodePaths = changes.split(',');
  const noChanges = changes === 'None';

  if (noChanges && additionalItems?.length) {
    return additionalItems.join(', ');
  }

  const itemsChanged = changedNodePaths
    .map((nodePath) => nodePathToFriendlyName[nodePath] ?? toCapitalized(nodePath))
    .join(', ');

  if (additionalItems?.length) {
    return `${itemsChanged}, ${additionalItems.join(', ')}`;
  }

  return itemsChanged;
}

export function parsePreviewChanges(changes, additionalItems) {
  const changedNodePaths = changes.split(',');
  const noChanges = changes === 'None';

  if (noChanges && additionalItems?.length) {
    return additionalItems.join(', ');
  }

  const itemsChanged = changedNodePaths
    .map((nodePath) => nodePathToFriendlyName[nodePath] ?? toCapitalized(nodePath))
    .join(', ');

  if (additionalItems?.length) {
    return `${itemsChanged}, ${additionalItems.join(', ')}`;
  }

  return itemsChanged;
}

// Optional-chained on purpose: the mutual brands' standardized reasons (the `M*` codes from #33591)
// have no entry here, and an unguarded `.find(...).boxTwoText` threw inside Preview's render body,
// white-screening the whole cancellation preview. A reason with no entry means no pre-determined
// note — not a crash.
export function determineBoxTwoText(cancelReason) {
  return cancelCodeInformationalArray.find((cancelCodeObject) => cancelCodeObject.cancelCode === cancelReason)
    ?.boxTwoText;
}

export function canAddToInternalNote(cancelReason) {
  return !!cancelCodeInformationalArray.find((cancelCodeObject) => cancelCodeObject.cancelCode === cancelReason)
    ?.allowFreeFormNote;
}

export const getButtonLabel = (action) => {
  if (action === PolicyChangeAction.Rescind) {
    return 'Rescind cancellation';
  }
  if (action === PolicyChangeAction.Reinstate) {
    return 'Reinstate policy';
  }
  if (action === PolicyChangeAction.Cancel) {
    return 'Cancel policy';
  }

  return 'Process endorsement';
};

export const DRAFT_ACTION = {
  DRAFT: 'draft',
  REVIEW: 'review',
  COMPLETE: 'complete'
};

const IN_REVIEW_LOCK_REASON = "This item is in underwriting review, so the policy can't be edited right now.";
const NEEDS_REVIEW_LOCK_REASON =
  'One or more changes made requires underwriting review before you can complete this change.';
const ALREADY_APPROVED_LOCK_REASON = 'This draft is already approved. Complete it to issue the endorsement.';
const APPROVE_FIRST_LOCK_REASON = 'Approve this draft before you complete it.';
const BLOCKING_UNAPPROVED_STATUSES = [DRAFT_STATUS.IN_REVIEW, DRAFT_STATUS.CHANGES_REQUESTED];
const NO_REVIEW_NEEDED_LOCK_REASON = 'None of the changes made require underwriting review at this time.';

/**
 * The next-step options for a draftable preview, each locked with the reason when the backend would refuse it. A change
 * that needs underwriting review is endorsed only once it is Approved, as confirmPolicyChange enforces; an unsaved
 * preview has no Approved record, so it is held to that too.
 */
export const getDraftActionOptions = ({ draftStatus, needsUnderwritingReview, canApproveUWReview }) => {
  const awaitsApproval = needsUnderwritingReview && draftStatus !== DRAFT_STATUS.APPROVED;
  const options = [
    { value: DRAFT_ACTION.DRAFT, label: 'Draft & keep editing' },
    {
      value: DRAFT_ACTION.REVIEW,
      label: canApproveUWReview
        ? 'Review underwriting questions and approve, deny, or request changes'
        : 'Send to underwriting for review'
    },
    { value: DRAFT_ACTION.COMPLETE, label: 'Complete the policy change' }
  ];

  if (canApproveUWReview) {
    // The backend refuses every review action on an Approved draft, and only the Approved draft can be completed
    // while a draft blocks the policy.
    return options.map((option) => {
      if (option.value === DRAFT_ACTION.REVIEW && draftStatus === DRAFT_STATUS.APPROVED) {
        return { ...option, disabled: true, lockedReason: ALREADY_APPROVED_LOCK_REASON };
      }
      if (
        option.value === DRAFT_ACTION.COMPLETE &&
        (BLOCKING_UNAPPROVED_STATUSES.includes(draftStatus) || awaitsApproval)
      ) {
        return { ...option, disabled: true, lockedReason: APPROVE_FIRST_LOCK_REASON };
      }
      return option;
    });
  }

  if (draftStatus === DRAFT_STATUS.IN_REVIEW) {
    return options.map((option) => ({ ...option, disabled: true, lockedReason: IN_REVIEW_LOCK_REASON }));
  }

  let lock = [DRAFT_ACTION.COMPLETE, NEEDS_REVIEW_LOCK_REASON];
  if (draftStatus === DRAFT_STATUS.APPROVED) {
    lock = [DRAFT_ACTION.REVIEW, ALREADY_APPROVED_LOCK_REASON];
  } else if (!needsUnderwritingReview) {
    lock = [DRAFT_ACTION.REVIEW, NO_REVIEW_NEEDED_LOCK_REASON];
  }
  const [lockedValue, lockedReason] = lock;

  return options.map((option) => (option.value === lockedValue ? { ...option, disabled: true, lockedReason } : option));
};

/** `draft.parentPreviewId` is set only when the save joined an earlier saved record of the same draft. */
export const getDraftSavedToastMessage = (draft) =>
  `${draft.parentPreviewId ? 'Draft updated' : 'Draft created'} — this is a copy of the policy. You're safe to keep working — this won't affect the live policy until you send it for review or complete it.`;

export const getDraftActionButtonLabel = (action) =>
  action === DRAFT_ACTION.COMPLETE ? 'Save policy changes' : 'Continue';

const isValuePresentInObject = (keys, obj, value) => {
  for (const key of keys) {
    if (obj.hasOwnProperty(key) && obj[key] === value) {
      return true;
    }
  }
  return false;
};

export const getNotes = ({ action, cancelReason, nonRenewReason, itemsChanged, diff }) => {
  const cancelBoxTwoText =
    action === PolicyChangeAction.Cancel &&
    cancelReason !== 'NA' &&
    (itemsChanged?.includes('cancelReason') || itemsChanged?.includes('endDate'))
      ? determineBoxTwoText(cancelReason)
      : undefined;

  if (cancelBoxTwoText) {
    //  some pre-determined notes based on cancel reasons cannot be edited
    const disableInternalNote = cancelBoxTwoText && !canAddToInternalNote(cancelReason);
    return { notesText: cancelBoxTwoText, cancelBoxTwoText, disableInternalNote };
  }

  const nonRenewBoxTwoText =
    nonRenewReason && nonRenewReason !== 'NA' && itemsChanged?.includes('autoRenew')
      ? determineBoxTwoText(nonRenewReason)
      : undefined;

  if (nonRenewBoxTwoText) {
    return { notesText: nonRenewBoxTwoText };
  }

  // Garage address rule exception approved
  const diffObj = diff && JSON.parse(diff);
  const addedChanges = diffObj?.full?.addedChanges;
  if (addedChanges) {
    const garageAddressRuleExceptionKeys = Object.keys(addedChanges).filter((k) =>
      k.includes('isGarageAddressRuleExceptionApproved')
    );
    const approvingGarageAddressRuleException = isValuePresentInObject(
      garageAddressRuleExceptionKeys,
      addedChanges,
      true
    );
    if (approvingGarageAddressRuleException) {
      return { notesText: 'Underwriting approved garage address rule exception' };
    }
  }

  return { notesText: '' };
};

export const getChangesForApproval = ({ policy, preview, session }) => {
  if (!preview || !policy || policy?.state !== 'MI' || policy.policyType !== 'A') {
    return {};
  }
  const {
    diff,
    createdBy: { isAgency: requestedByIA }
  } = preview;
  const policyDetails = policyDetailsFromSegments(policy);
  const { canApproveUWReview } = session;

  const diffObj = diff && JSON.parse(diff);
  const addedChanges = diffObj?.full?.addedChanges || {};
  const removedChanges = diffObj?.full?.removedChanges || {};

  // dont run for cancels
  if (addedChanges?.cancelled === true) {
    return {};
  }

  const {
    needsProofOfEligibilityDocuments: showProofOfEligibilityDocumentsBlock,
    householdResidentsChanged: showAllHouseholdResidentsIncludedBlock,
    needsNewPIPForm: showNewPIPFormRequiredBlock,
    hasChangesThatNeedApproval
  } = getMiAutoPipChanges({
    addedChanges,
    removedChanges,
    policyDetails,
    canApproveUWReview,
    isAgency: requestedByIA
  });

  return {
    showProofOfEligibilityDocumentsBlock,
    showAllHouseholdResidentsIncludedBlock,
    showNewPIPFormRequiredBlock,
    hasChangesThatNeedApproval
  };
};

// #34681: the auto-side umbrella requirements an estimate-only preview skipped, for the preview callout
export const getUnmetUmbrellaAutoRequirements = ({
  autoPolicyDetails,
  hasActiveOrFutureAutoPolicy,
  renewalVariables,
  state,
  preview
}) => {
  const uninsuredMotoristLimit =
    preview &&
    policyDetailsFromSegments(preview.policy, {
      activeOnDate: preview.changeDetails.effectiveDate,
      ignoreRenewalSegments: false
    }).umbrellaCoverage?.uninsuredMotoristLimit;
  const { requirements } = getUmbrellaEligibility({
    ...autoPolicyDetails?.autoCoverage,
    state,
    uninsuredMotoristLimit,
    policyLimitBIPD: autoPolicyDetails?.autoCoverage?.policyLimitBIPD,
    drivers: autoPolicyDetails?.drivers,
    renewalVariables
  });
  const biMinimum = getUmbrellaBIMinimum(renewalVariables);
  return [
    !hasActiveOrFutureAutoPolicy && !renewalVariables?.allowUmbrella && 'An active auto policy',
    !requirements.autoBILimit &&
      (biMinimum === 500
        ? 'Auto BI of at least $500K per occurrence ($250K/$500K split)'
        : `Auto BI of at least $${biMinimum}K per occurrence`),
    !requirements.noExcludedDrivers && 'No excluded drivers',
    !requirements.noMajorViolationOrUDR && 'No drivers with a major violation or unverifiable driving record',
    !requirements.noAtFaultAccidentBlockingUmbrella &&
      'No drivers with an at-fault accident that disqualifies them from umbrella',
    !requirements.autoUMUIM &&
      'Auto uninsured and underinsured motorist limits of at least $250K/$500K split or $500K CSL'
  ].filter(Boolean);
};
